import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import {
  DataService,
  IPersistable,
  PersistenceService,
  PersistMode,
  ReadPicklistItemsModel,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import {
  AbcDatepickerComponent,
  AbcDropDownBoxComponent,
  AbcInputComponent,
  AbcTextAreaComponent,
  AbcTextBoxComponent,
} from '@barthauer/abc/ui-components';
import { forkJoin, Observable, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { DocInfo, DocumentModel } from '../../../models/document.model';
import { DocumentsEditorsService } from '../../../services/documents-editors.service';
import { ValueWrapper, ValueWrapperViewModel } from '../../../services/valueWrapper';
import { ValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { CompareHelper } from '@barthauer/abc/core';

@Component({
  selector: 'test-app-documents-editor-card-details',
  templateUrl: './documents-editor-card-details.component.html',
  styleUrls: ['./documents-editor-card-details.component.scss'],
  standalone: false,
})
export class DocumentsEditorCardDetailsComponent implements OnInit {
  @ViewChild('designationInput', { static: true }) designationInput: AbcInputComponent;
  @ViewChild('dateInput', { static: true }) dateInput: AbcDatepickerComponent;
  @ViewChild('numberInput', { static: true }) numberInput: AbcInputComponent;
  @ViewChild('programPathInput', { static: true }) programPathInput: AbcInputComponent;
  @ViewChild('fileNameUploaderTextBox', { static: true }) fileNameUploaderTextBox: AbcTextBoxComponent;
  @ViewChild('validFromInput', { static: true }) validFromInput: AbcDatepickerComponent;
  @ViewChild('validUntilInput', { static: true }) validUntilInput: AbcDatepickerComponent;
  @ViewChild('Comment', { static: true }) Comment: AbcTextAreaComponent;
  @ViewChild('abc', { static: true, read: ViewContainerRef }) abc: ViewContainerRef;
  @ViewChild('documentKindDropDownBox', { static: true }) documentKindDropDownBox: AbcDropDownBoxComponent;
  @ViewChild('dataCarrierDropDownBox', { static: true }) dataCarrierDropDownBox: AbcDropDownBoxComponent;
  objectKey;
  private numberValue: number;
  public dataCarrierValue: Date;
  private programPathValue: string;
  private validFromInputValue: Date;
  private validUntilInputValue: Date;
  private commentValue: string;
  private listOfValueWrappers = {};
  private componentContainer = [];
  public documentKindData: { Description: string; Value: string | number }[];
  public documentCarrierData: { Description: string; Value: string | number }[];
  private unsubscribe$ = new Subject<void>();
  public document: DocumentModel;
  public displayDetailsFields: boolean;
  public isOkBtnDisabled: boolean = true;
  public baseUrl: string;
  public customFileName: string;
  private newFile: DocInfo;
  private fileToUpload: any;
  private persistable: IPersistable;
  @Input() private _docInfo;
  public get docInfo() {
    return this._docInfo;
  }
  public res = ResourceManager.getResources('abc-common-editors-documents');
  public filename: string = '';
  public set docInfo(value) {
    this.displayDetailsFields = true;
    if (value) {
      this.document = value;
      this.objectKey.id = value.Id;

      if (!value.FileName && value.Designation) {
        value.FileName = value.Designation;
      }
      this.filename = value.FileName || '';
      if (value?.BinaryUrl) {
        this.baseUrl = value.BinaryUrl;
      }
      if (this.document?.Id) {
        this.addObjectForMerge();
      }
    } else {
      this.displayDetailsFields = false;
      this.filename = '';
    }
  }

  constructor(
    private persistenceService: PersistenceService,
    private dataService: DataService,
    private documentsEditorsService: DocumentsEditorsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.objectKey = new ValueWrapperViewModel(this.persistenceService);
    forkJoin({
      kindResult: this.queryPicklistItems('Dokumentart').pipe(first()),
      carrierResult: this.queryPicklistItems('Bildspeichermedium').pipe(first()),
    })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(({ kindResult, carrierResult }) => {
        this.documentKindData = kindResult.Data;
        this.documentCarrierData = carrierResult.Data;
        if (this.document) {
          this.fillFields(this.document);
        }
      });
    this.componentContainer.push(
      this.designationInput,
      this.dateInput,
      this.fileNameUploaderTextBox,
      this.documentKindDropDownBox,
      this.dataCarrierDropDownBox,
      this.numberInput,
      this.programPathInput,
      this.validFromInput,
      this.validUntilInput,
      this.Comment,
    );
    this.objectKey.objectType = 'Document';
  }

  ngAfterViewInit(): void {
    const el = document.querySelector('.dx-fileuploader-button>.dx-button-content');
    el.outerHTML = '<i class="material-icons dx-icon">cloud_upload</i>';
  }

  public cancelButton(): void {
    this.documentsEditorsService.callCloseDialogSubject();
  }

  public onNumberchange(event: any) {
    this.numberValue = event.newValue;
  }

  public onDataCarrierChange(event: any) {
    this.dataCarrierValue = event.newValue;
  }
  public onProgramPathchange(event: any) {
    this.programPathValue = event.newValue;
  }
  public onValidFromInputchange(event: any) {
    this.validFromInputValue = event.newValue;
  }
  public onValidUntilInputchange(event: any) {
    this.validUntilInputValue = event.newValue;
  }

  public onCommentChange(event: any) {
    this.commentValue = event.newValue;
  }

  public okButton(): void {
    if (this.fileToUpload?.docDetails) {
      this.fileToUpload.docDetails.Designation = this.customFileName ?? this.designationInput.value;
      this.fileToUpload.docDetails.FileName = this.fileNameUploaderTextBox.value;
      this.fileToUpload.docDetails.Date = this.dateInput.value;
      this.fileToUpload.docDetails.Kind = this.documentKindDropDownBox.value;
    }

    this.documentsEditorsService.callUpdateDataSubject({
      designation: this.customFileName,
      fileToUpload: this.fileToUpload,
      documentId: this.document?.Id ?? null,
      numberValue: this.numberValue,
      dataCarrierValue: this.dataCarrierValue,
      programPathValue: this.programPathValue,
      validFromInputValue: this.validFromInputValue,
      validUntilInputValue: this.validUntilInputValue,
      commentValue: this.commentValue,
    });
    this.documentsEditorsService.callPersistPendingChanges();
    this.documentsEditorsService.callCloseDialogSubject();
  }

  public resetButton(): void {
    this.persistenceService.revertPendingChanges();
  }

  public uploadFileData(event: ValueChangedEvent): void {
    const file = event.value[0];
    const reader = new FileReader();
    reader.readAsDataURL(new Blob([file]));
    reader.onloadend = () => {
      const data = { docDetails: file, baseUrl: reader.result };
      this.fileToUpload = data;
      this.newFile = new DocInfo(data.docDetails);
      this.designationInput.value = this.customFileName ?? this.newFile.Designation;
      this.dateInput.value = new Date();
      let label = this.documentsEditorsService.getFileType(this.newFile.Designation, false, true);
      let value = this.documentKindData.find((_) => _.Description === label)?.Value;

      if (CompareHelper.isNullorEmpty(value)) {
        label = this.documentsEditorsService.getFileType(this.newFile.Designation, false, false);
        value = this.documentKindData.find((_) => _.Description === label)?.Value;
      }

      this.documentKindDropDownBox.value = value;
      this.fileNameUploaderTextBox.value = this.newFile.FileName;
      this.isOkBtnDisabled = false;
      this.fillListOfValueWrappers(this.newFile);
    };
  }

  private fillFields(data): void {
    this.designationInput.value = data.Designation;
    this.dateInput.value = data.Date;
    this.fileNameUploaderTextBox.value = data.FileName;
    this.numberInput.value = data.Number;
    this.programPathInput.value = data.ProgramPath;
    this.validFromInput.value = data.DateStart;
    this.validUntilInput.value = data.DateEnd;
    this.Comment.value = data.Comment;
    this.documentKindDropDownBox.value = this.documentKindData.find((_) => _.Description === data.Kind)?.Value;
    this.dataCarrierDropDownBox.value = data.Media;

    this.isOkBtnDisabled = false;
    this.fillListOfValueWrappers(data);
    this.changeDetectorRef.detectChanges();
  }

  private queryPicklistItems(parameter): Observable<any> {
    const observable$ = new Observable((observer) => {
      const queryPicklistItems: ReadPicklistItemsModel = new ReadPicklistItemsModel();
      queryPicklistItems.Indent = true;
      queryPicklistItems.OutputMode = 0;
      queryPicklistItems.Name = parameter;
      this.dataService.executeReadPicklistsItemsQuery(queryPicklistItems).subscribe((result) => {
        observer.next(result);
      });
    });
    return observable$;
  }

  private fillListOfValueWrappers(data): void {
    for (const property in data) {
      this.listOfValueWrappers[property] = new ValueWrapper(
        property,
        data[property],
        this.objectKey,
        this.persistenceService,
      );
    }
    this.objectKey.listOfValueWrappers = this.listOfValueWrappers;
    this.objectKey.abcComponentContainer = this.componentContainer;
  }

  private addObjectForMerge() {
    this.persistable = {
      id: this.document.Id,
      objectType: 'Document',
      persistenceCategory: this.documentsEditorsService.persistenceCategory,
      changesPersisted: (persistMode) => (persistMode = PersistMode.Update),
      revertChanges: () => {},
    };
    this.persistenceService.addObjectValue(this.persistable, 'Designation', this.document.Designation);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
